import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Upload, Folder, FolderPlus, Image as ImageIcon, Trash2, ChevronRight, Copy, Search } from "lucide-react";
import { toast } from "sonner";

export default function CasaMadreMedia() {
  const [data, setData] = useState({ items: [], current_folder: "", base_url: "" });
  const [folder, setFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const load = async (f = folder) => {
    const r = await api.get(`/media/library?folder=${encodeURIComponent(f)}`);
    setData(r.data);
  };
  useEffect(() => { load(); }, [folder]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("folder", folder);
      const r = await api.post("/media/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.saved.length} file caricati`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const createFolder = async () => {
    const name = window.prompt("Nome cartella:");
    if (!name) return;
    await api.post("/media/library/folder", { name, parent: folder });
    toast.success("Cartella creata");
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    const p = folder ? `${folder}/${item.name}` : item.name;
    await api.delete(`/media/library?path=${encodeURIComponent(p)}`);
    toast.success("Eliminato");
    load();
  };

  const copyName = (item) => {
    navigator.clipboard.writeText(item.name);
    toast.success(`"${item.name}" copiato — incolla nel CSV`);
  };

  const filtered = q ? data.items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase())) : data.items;
  const folders = filtered.filter((i) => i.is_folder);
  const files = filtered.filter((i) => !i.is_folder);

  const crumbs = folder ? folder.split("/").filter(Boolean) : [];

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8" data-testid="cm-media-page">
        <div className="mb-6">
          <div className="overline text-brand mb-1">Casa Madre · Archivio</div>
          <h1 className="font-heading font-bold text-3xl flex items-center gap-2"><ImageIcon className="w-7 h-7" />Media Library</h1>
          <p className="text-sm text-muted-foreground mt-1">Foto prodotti, tessuti, piedi, video. I file caricati qui vengono ereditati automaticamente dai tuoi rivenditori. Nei CSV usa solo il nome file (es. <code className="bg-neutral-100 px-1 rounded text-xs">barka-front.jpg</code>).</p>
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-border rounded-sm p-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="text-sm flex items-center gap-1 flex-1 min-w-0">
            <button onClick={() => setFolder("")} className="hover:text-brand">/</button>
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                <button onClick={() => setFolder(crumbs.slice(0, i + 1).join("/"))} className="hover:text-brand">{c}</button>
              </span>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca file..." data-testid="media-search"
              className="h-9 pl-9 pr-3 w-56 rounded-sm border border-border text-sm" />
          </div>
          <button onClick={createFolder} data-testid="media-new-folder"
            className="h-9 px-3 rounded-sm border border-border text-sm hover:bg-accent inline-flex items-center gap-1.5">
            <FolderPlus className="w-4 h-4" /> Nuova cartella
          </button>
          <label className="h-9 px-3 rounded-sm bg-foreground text-white text-sm hover:bg-brand inline-flex items-center gap-1.5 cursor-pointer">
            <Upload className="w-4 h-4" /> {uploading ? "Caricamento..." : "Carica file"}
            <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={(e) => upload(e.target.files)} className="hidden" data-testid="media-upload" />
          </label>
        </div>

        {/* Grid */}
        <div className="bg-white border border-border rounded-sm p-4">
          {data.items.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Archivio vuoto. Trascina file qui o usa &quot;Carica file&quot;.</p>
            </div>
          ) : (
            <>
              {folders.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
                  {folders.map((f) => (
                    <button key={f.path} onClick={() => setFolder(f.path)} data-testid={`folder-${f.name}`}
                      className="p-3 border border-border rounded-sm hover:bg-accent text-left">
                      <Folder className="w-6 h-6 text-amber-600 mb-1" />
                      <div className="text-xs font-medium truncate">{f.name}</div>
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {files.map((f) => {
                  const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(f.name);
                  return (
                    <div key={f.path} className="group relative border border-border rounded-sm overflow-hidden bg-neutral-50" data-testid={`file-${f.name}`}>
                      <div className="aspect-square bg-neutral-100 flex items-center justify-center overflow-hidden">
                        {isImg ? <img src={f.url} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                                : <div className="text-xs text-muted-foreground p-2 text-center">{f.name.split(".").pop().toUpperCase()}</div>}
                      </div>
                      <div className="p-2">
                        <div className="text-[10px] font-mono truncate" title={f.name}>{f.name}</div>
                        <div className="text-[9px] text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</div>
                      </div>
                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => copyName(f)} title="Copia nome per CSV" data-testid={`copy-${f.name}`}
                          className="w-7 h-7 rounded-sm bg-white border border-border hover:bg-foreground hover:text-white"><Copy className="w-3.5 h-3.5 mx-auto" /></button>
                        <button onClick={() => remove(f)} title="Elimina" data-testid={`del-${f.name}`}
                          className="w-7 h-7 rounded-sm bg-white border border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mx-auto" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-border text-xs text-muted-foreground">
                {files.length} file · {folders.length} cartelle · Base URL: <code className="bg-neutral-100 px-1 rounded">{data.base_url}</code>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
